import React, { useState } from 'react';
import moment from 'moment';
import OrderSummary from '../../dashboard/components/order-summary';
import PassbookService from '../../../services/passbook.service';
import Loader from '../../../shared/common/loader';
import { formatAmount, getTransactionName } from '../../../utils';
import { userStore } from 'store/UserStore';
import '../table.css';

interface PropsType {
  walletData: any[];
}

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  APPROVED: { bg: '#E6F4EC', color: '#1A6637' },
  PENDING: { bg: '#FEF6E0', color: '#7A5010' },
  REJECTED: { bg: '#FDEAEA', color: '#961B1B' },
  'APPROVAL PENDING': { bg: '#E6EEFF', color: '#1E3A8A' },
  'VERIFICATION PENDING': { bg: '#F1F2F4', color: '#5C6470' },
};

const getStatusStyle = (status: string) =>
  STATUS_COLORS[status] ?? { bg: '#F1F2F4', color: '#5C6470' };

const mapStatus = (raw: string): string => {
  switch (raw) {
    case 'ACCEPTED':
      return 'APPROVED';
    case 'REJECTED':
      return 'REJECTED';
    case 'TRANSACTION_APPROVAL_PENDING':
      return 'APPROVAL PENDING';
    case 'TRANSACTION_VERIFICATION_PENDING':
      return 'VERIFICATION PENDING';
    default:
      return 'PENDING';
  }
};

// ─── Amount sign helper ───────────────────────────────────────────────────────

const getAmountSign = (record: any): string => {
  const { type, method } = record;
  if (type === 'ORDER_SELL') {
    if (method === 'WALLET') return '-';
    if (method === 'UPI' || method === 'NEBK') return '+';
  }
  if (type === 'WALLET') {
    if (method === 'UPI' || method === 'NEBK') return '+';
    if (method === 'NEFT') return '-';
  }
  if (type === 'ORDER_BUYBACK') {
    if (
      method === 'VERIFICATION' ||
      method === 'ORDER_REFUND' ||
      method === 'WALLET' ||
      method === 'NEFT'
    )
      return '+';
  }
  return '';
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusBadge = ({ rawStatus }: { rawStatus: string }) => {
  const label = mapStatus(rawStatus);
  const sc = getStatusStyle(label);
  return (
    <span
      className="txn-status-badge"
      style={{ background: sc.bg, color: sc.color }}
    >
      {label}
    </span>
  );
};

const TypeBadge = ({ type }: { type: string }) => {
  if (type === 'ORDER_SELL')
    return <span className="txn-type-badge buy">BUY</span>;
  if (type === 'ORDER_BUYBACK')
    return <span className="txn-type-badge sell">SELL</span>;
  return <span className="txn-type-badge" style={{ background: '#F1F2F4', color: '#5C6470' }}>{type}</span>;
};

const AmountCell = ({ record }: { record: any }) => {
  const sign = getAmountSign(record);
  const isPositive = sign === '+';
  const isNegative = sign === '-';

  return (
    <span
      className="txn-amount-parent"
      style={{
        color: isPositive ? '#1A6637' : isNegative ? '#961B1B' : '#111827',
      }}
    >
      {sign && <span style={{ marginRight: 1 }}>{sign}</span>}
      ₹{formatAmount(record.amount)}
    </span>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const WalletTransactions: React.FC<PropsType> = ({ walletData }) => {
  const [customer, user] = userStore((state: any) => [
    state.customer,
    state.user,
  ]);

  const [selectedWalletData, setSelectedWalletData] = useState<any>(null);
  const [isShow, setIsShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const onHandleSideModal = (v: boolean) => setIsShow(v);

  const getOrderDetails = async (orderRef: string) => {
    try {
      setLoading(true);
      const res: any = await PassbookService.getOrderDetails(
        orderRef,
        user?.userId
      );
      if (res.status === 200 && res.data.data.result?.length > 0) {
        setSelectedWalletData(res.data.data.result[0]);
        onHandleSideModal(true);
      }
    } catch (e: any) {
      console.log(e.message);
    } finally {
      setLoading(false);
    }
  };

  const canView = (type: string) =>
    type === 'ORDER_SELL' || type === 'ORDER_BUYBACK';

  return (
    <>
      {selectedWalletData && (
        <OrderSummary
          handleModal={onHandleSideModal}
          isShow={isShow}
          data={selectedWalletData}
          isPassbookRedirect={false}
          displaySummary={true}
          displayInvoice={
            customer?.customerCategory !== 'REGISTERED' ||
            selectedWalletData?.type !== 'BUYBACK'
          }
        />
      )}
      {loading && <Loader />}

      <div className="txn-wrapper">
        <div className="txn-scroll">
          <table className="txn-table">
            <colgroup>
              <col style={{ width: '18%' }} /> {/* Transaction ID */}
              <col style={{ width: '7%'  }} /> {/* Type            */}
              <col style={{ width: '25%' }} /> {/* Transaction     */}
              <col style={{ width: '16%' }} /> {/* Date            */}
              <col style={{ width: '14%' }} /> {/* Status          */}
              <col style={{ width: '12%' }} /> {/* Amount          */}
              <col style={{ width: '8%'  }} /> {/* Action          */}
            </colgroup>

            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Type</th>
                <th>Transaction</th>
                <th>Date</th>
                <th>Status</th>
                <th className="right">Amount</th>
                <th className="center">Action</th>
              </tr>
            </thead>

            <tbody>
              {walletData.length === 0 && (
                <tr>
                  <td colSpan={7} className="txn-empty">
                    <div className="txn-empty-icon">🪙</div>
                    <div className="txn-empty-text">
                      No wallet transactions found
                    </div>
                  </td>
                </tr>
              )}

              {walletData.map((transaction: any, index: number) => (
                <tr
                  key={`wallet-${transaction.transactionMasterId}-${index}`}
                  className="txn-row-single"
                >
                  {/* Transaction ID */}
                  <td>
                    <span className="txn-order-id">
                      {transaction.transactionMasterId}
                    </span>
                  </td>

                  {/* Type badge */}
                  <td>
                    <TypeBadge type={transaction.type} />
                  </td>

                  {/* Transaction name */}
                  <td>
                    <div className="txn-order-cell">
                      <div className="txn-order-line">
                        <span className="txn-metal-name">
                          {getTransactionName(transaction)}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Date */}
                  <td>
                    <div className="txn-date">
                      {moment(
                        transaction.date,
                        ['DD-MM-YYYY hh:mm:ss']
                      ).format('DD MMM YYYY')}
                    </div>
                    <div className="txn-time">
                      {moment(
                        transaction.date,
                        ['DD-MM-YYYY hh:mm:ss']
                      ).format('hh:mm A')}
                    </div>
                  </td>

                  {/* Status */}
                  <td>
                    <div className="txn-status-stack">
                      <StatusBadge rawStatus={transaction.status} />
                    </div>
                  </td>

                  {/* Amount */}
                  <td className="right">
                    <AmountCell record={transaction} />
                  </td>

                  {/* Action */}
                  <td className="center">
                    {canView(transaction.type) && (
                      <button
                        className="txn-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          getOrderDetails(transaction.extRefId);
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M8 7V3M16 7V3M3 11H21M5 5H19C20.1 5 21 5.9 21 7V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V7C3 5.9 3.9 5 5 5Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default WalletTransactions;